import type { TrackBatchEvent } from '@volt/shared';
import type { ApiClient } from '../api/client.js';

export interface TrackingBuffer {
  enqueue: (event: TrackBatchEvent) => void;
  flush: (options?: { keepalive?: boolean }) => Promise<void>;
  destroy: () => void;
}

const MAX_QUEUE_SIZE = 200;

export function createTrackingBuffer(
  api: ApiClient,
  options: { flushIntervalMs?: number; flushSize?: number } = {},
): TrackingBuffer {
  const flushIntervalMs = options.flushIntervalMs ?? 5000;
  const flushSize = options.flushSize ?? 20;
  let queue: TrackBatchEvent[] = [];
  let flushing: Promise<void> | null = null;

  const flush = async (flushOptions: { keepalive?: boolean } = {}): Promise<void> => {
    while (true) {
      if (flushing) await flushing;
      if (queue.length === 0) return;
      const batch = queue.splice(0, queue.length);
      flushing = api
        .trackBatch({ events: batch }, flushOptions)
        .catch(() => {
          queue = [...batch, ...queue].slice(0, MAX_QUEUE_SIZE);
        })
        .finally(() => {
          flushing = null;
        });
      await flushing;
      if (queue.length === 0) return;
    }
  };

  const timer = globalThis.setInterval(() => {
    void flush();
  }, flushIntervalMs);

  return {
    enqueue(event) {
      queue.push(event);
      if (queue.length > MAX_QUEUE_SIZE) queue = queue.slice(-MAX_QUEUE_SIZE);
      if (queue.length >= flushSize) void flush();
    },
    flush,
    destroy() {
      globalThis.clearInterval(timer);
      void flush({ keepalive: true });
    },
  };
}
